import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ApolloError } from '@apollo/client';
import { css, keyframes } from '@emotion/css';
import {
  Box,
  Button,
  LinearProgress,
  Menu,
  MenuItem,
  TablePagination,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from '@mui/material';
import { BiChevronDown, BiPlus, BiSolidGridAlt, BiTable } from 'react-icons/bi';

import DefaultFallback from '@components/Fallbacks/DefaultFallback';
import Spacer from '@components/Utils/Spacer';
import Toolbar from '@components/Utils/Toolbar';

import { useRooftop } from '@store';
import { capabilityGate } from '@hopdrive/feature-portal-products';
import { CONSUMER_PAY_ENABLED } from '@hopdrive/sdk-appointments';
import { useProductEntry } from '@features/products/useProductEntry';

import AppointmentsTable from './AppointmentsTable';
import AppointmentsGrid from './AppointmentsGrid';
import AddAppointmentDialog from './AddAppointmentDialog';
import AppointmentsFilter from './AppointmentsFilter';
import { GetMobileServiceAppointmentsQuery } from '../../mobileService/queries/getMobileServiceAppointments.query.generated';

type Appointment = GetMobileServiceAppointmentsQuery['appointments'][number];
type ViewMode = 'table' | 'tiles';

interface AppointmentsContentProps {
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => Promise<void>;
  appointments: Appointment[];
  totalCount: number;
  offset: number;
  limit: number;
  setOffset: (v: number) => void;
  setLimit: (v: number) => void;
  orderBy: string;
  order: 'asc' | 'desc';
  setOrderBy: (v: string) => void;
  setOrder: (v: 'asc' | 'desc') => void;
  range: string;
  start: string;
  end: string;
  onRangeChange: (value: string) => void;
  onDateChange: (value: string, name: string) => void;
  search: string;
  setSearch: (value: string) => void;
}

const AppointmentsContent: React.FC<AppointmentsContentProps> = ({
  loading,
  error,
  refetch,
  appointments,
  totalCount,
  offset,
  limit,
  setOffset,
  setLimit,
  orderBy,
  order,
  setOrderBy,
  setOrder,
  range,
  start,
  end,
  onRangeChange,
  onDateChange,
  search,
  setSearch,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const cls = useStyles(theme);

  const rooftop = useRooftop();
  // The Mobile Service flow is a creation: offered when this viewer may create in the product
  // (absent until the products load).
  const canCreateMobileService = capabilityGate(useProductEntry('mobile-service'), 'createNew').kind === 'render';
  // The Valet form is the consumer-pay offer, so it is only offered while
  // consumer-pay is switched on.
  const isValetEnabled =
    CONSUMER_PAY_ENABLED &&
    (rooftop?.effective_config as { appointments?: { enabled?: boolean } } | undefined)?.appointments?.enabled === true;

  const [addDialogOpen, setAddDialogOpen] = React.useState(false);
  const [addMenuAnchor, setAddMenuAnchor] = React.useState<HTMLElement | null>(null);
  const [viewMode, setViewMode] = React.useState<ViewMode>(() => {
    return (localStorage.getItem('appointments-view-mode') as ViewMode) || 'table';
  });

  const handleAddClick = (e: React.MouseEvent<HTMLElement>) => {
    // If both types are available, show the menu. Otherwise jump straight to the
    // only available flow.
    if (canCreateMobileService && isValetEnabled) {
      setAddMenuAnchor(e.currentTarget);
    } else if (canCreateMobileService) {
      setAddDialogOpen(true);
    } else if (isValetEnabled) {
      navigate('/appointments/add');
    }
  };

  const handlePickMobileService = () => {
    setAddMenuAnchor(null);
    setAddDialogOpen(true);
  };

  const handlePickValet = () => {
    setAddMenuAnchor(null);
    navigate('/appointments/add');
  };

  const handleViewModeChange = (_e: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
    if (newMode !== null) {
      localStorage.setItem('appointments-view-mode', newMode);
      setViewMode(newMode);
    }
  };

  const [loadingVisible, setLoadingVisible] = React.useState(false);
  React.useEffect(() => {
    if (loading) {
      setLoadingVisible(true);
    } else {
      setTimeout(() => setLoadingVisible(false), 250);
    }
  }, [loading]);

  return (
    <>
      <AddAppointmentDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={() => { setAddDialogOpen(false); refetch(); }}
      />

      <div className={cls.header}>
        <Toolbar
          fullscreen
          zIndex={0}
          position='relative'
          title='Appointments'
          refetch={refetch}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <ToggleButtonGroup
              exclusive
              value={viewMode}
              onChange={handleViewModeChange}
              aria-label='view mode'
              size='small'
            >
              <ToggleButton value='table' aria-label='table view' color='utility'>
                <BiTable style={{ marginRight: 6 }} />
                Table
              </ToggleButton>
              <ToggleButton value='tiles' aria-label='tile view' color='primary'>
                <BiSolidGridAlt style={{ marginRight: 6 }} />
                Tiles
              </ToggleButton>
            </ToggleButtonGroup>

            <Box sx={{ flex: 1 }} />

            {(canCreateMobileService || isValetEnabled) && (
              <Button
                variant='contained'
                size='small'
                startIcon={<BiPlus />}
                endIcon={canCreateMobileService && isValetEnabled ? <BiChevronDown /> : undefined}
                onClick={handleAddClick}
                sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
              >
                New Appointment
              </Button>
            )}
            <Menu
              anchorEl={addMenuAnchor}
              open={Boolean(addMenuAnchor)}
              onClose={() => setAddMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={handlePickMobileService}>Mobile Service</MenuItem>
              <MenuItem onClick={handlePickValet}>Valet</MenuItem>
            </Menu>
          </Box>
        </Toolbar>

        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <AppointmentsFilter
            refetch={refetch}
            range={range}
            start={start}
            end={end}
            onRangeChange={onRangeChange}
            onDateChange={onDateChange}
            disablePickers={range !== 'custom'}
            search={search}
            setSearch={setSearch}
          />
        </Box>
      </div>

      {loadingVisible ? (
        <LinearProgress className={loading ? cls.fadeIn : cls.fadeOut} />
      ) : (
        <div className={cls.loadingPad} />
      )}

      {error ? (
        <>
          <Spacer size={2} />
          <DefaultFallback fullscreen severity='error' message='Failed to fetch appointments.' />
        </>
      ) : null}

      {!loading && !error && appointments.length === 0 ? (
        <>
          <Spacer size={2} />
          <DefaultFallback
            fullscreen
            severity='warning'
            message='No appointments found. Try adjusting your date range or create a new appointment.'
          />
        </>
      ) : null}

      {!error && appointments.length > 0 ? (
        <>
          <TablePagination
            className={cls.pagination}
            component='div'
            count={totalCount}
            page={Math.floor(offset / limit)}
            onPageChange={(_e, p) => setOffset(p * limit)}
            rowsPerPage={limit}
            onRowsPerPageChange={e => { setLimit(parseInt(e.target.value, 10)); setOffset(0); }}
            rowsPerPageOptions={[25, 50, 100]}
          />

          <div className={loading ? cls.contentLoading : cls.content}>
            {viewMode === 'table' ? (
              <AppointmentsTable
                appointments={appointments}
                totalCount={totalCount}
                orderBy={orderBy}
                order={order}
                setOrderBy={setOrderBy}
                setOrder={setOrder}
                setOffset={setOffset}
              />
            ) : (
              <AppointmentsGrid appointments={appointments} />
            )}

            <TablePagination
              className={cls.pagination}
              component='div'
              count={totalCount}
              page={Math.floor(offset / limit)}
              onPageChange={(_e, p) => setOffset(p * limit)}
              rowsPerPage={limit}
              onRowsPerPageChange={e => { setLimit(parseInt(e.target.value, 10)); setOffset(0); }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </div>
        </>
      ) : null}
    </>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme) => {
  const fadeIn = keyframes`
    0% { opacity: 0; }
    100% { opacity: 1; }
  `;

  const fadeOut = keyframes`
    0% { opacity: 1; }
    100% { opacity: 0; }
  `;

  const contentFadeIn = keyframes`
    0% { opacity: 0; }
    33% { opacity: 0; }
    100% { opacity: 1; }
  `;

  return {
    header: css`
      background-color: ${theme.palette.background.paper};
      border-bottom: 1px solid ${theme.palette.divider};
    `,
    fadeIn: css`
      opacity: 1;
      animation: ${fadeIn} 0.25s ease-in-out;
    `,
    fadeOut: css`
      opacity: 0;
      animation: ${fadeOut} 0.25s ease-in-out;
    `,
    loadingPad: css`
      width: 100%;
      height: 4px;
    `,
    content: css`
      opacity: 1;
      transition: opacity 0.25s ease-in-out;
      animation: ${contentFadeIn} 0.5s ease-in-out;
    `,
    contentLoading: css`
      opacity: 0;
      transition: opacity 0.25s ease-in-out;
      user-select: none;
      pointer-events: none;
    `,
    pagination: css`
      border-bottom: 1px solid ${theme.palette.divider};
      margin-right: 16px;
    `,
  };
};

export default AppointmentsContent;
